import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type {
  CalendarState,
  ConfirmDuplicateActivityPayload,
  DeleteActivityPayload,
  DuplicateActivityPayload,
  EditActivityModalOpenPayload,
  MoveWeekPayload,
} from './calendarTypes';

const initialState: CalendarState = {
  calendarKeys: [],
  merge: true,
  unselectedClasses: [],
  selectionMode: false,
  selectionOptions: {
    selectionType: null,
    confirmLabel: null,
    cancelLabel: null,
    scrollPosition: null,
  },
  editActivityModal: {
    status: 'CLOSED',
    data: null,
  },
  duplicateActivityModal: {
    status: 'CLOSED',
    data: null,
  },
  scrollToDate: null,
};

const calendarState = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    loadGoalsForPlans: (_state, _action) => {},
    loadTranslationsToNewCurricula: (_state, _action) => {},
    setCalendarKeys: (state, action: PayloadAction<{ calendarKeys: string[] }>) => {
      const { calendarKeys } = action.payload;
      state.calendarKeys = calendarKeys;
      state.unselectedClasses = [];
    },
    setMerge: (state, action: PayloadAction<{ merge: boolean }>) => {
      const { merge } = action.payload;
      state.merge = merge;
    },
    selectClass: (state, action: PayloadAction<{ classHref: string }>) => {
      const { classHref } = action.payload;
      const index = state.unselectedClasses.indexOf(classHref);
      if (index !== -1) state.unselectedClasses.splice(index, 1);
    },
    unselectClass: (state, action: PayloadAction<{ classHref: string }>) => {
      const { classHref } = action.payload;
      const index = state.unselectedClasses.indexOf(classHref);
      if (index === -1) state.unselectedClasses.push(classHref);
    },
    setSelectedClasses: (
      state,
      action: PayloadAction<{ classes: Array<{ href: string; selected: boolean }> }>,
    ) => {
      const { classes } = action.payload;
      classes.forEach((clss) => {
        const classHref = clss.href;
        if (clss.selected) {
          const index = state.unselectedClasses.indexOf(classHref);
          if (index !== -1) state.unselectedClasses.splice(index, 1);
        } else {
          const index = state.unselectedClasses.indexOf(classHref);
          if (index === -1) state.unselectedClasses.push(classHref);
        }
      });
    },
    setUnSelectedClasses: (state, action: PayloadAction<{ unselectedClasses: string[] }>) => {
      const { unselectedClasses } = action.payload;
      state.unselectedClasses = unselectedClasses;
    },
    deleteActivity: (state, _action: DeleteActivityPayload) => {
      state.editActivityModal.status = 'SAVING';
    },
    saveActivity: (state, _action: PayloadAction<unknown>) => {
      state.editActivityModal.status = 'SAVING';
    },
    setSelectionMode: (state, action: PayloadAction<boolean>) => {
      state.selectionMode = !!action.payload;
    },
    setSelectionOptions: (
      state,
      action: PayloadAction<{
        selectionType?: string;
        confirmLabel?: string;
        cancelLabel?: string;
        scrollPosition?: string;
      }>,
    ) => {
      const { selectionType, confirmLabel, cancelLabel, scrollPosition } = action.payload;
      if (selectionType) state.selectionOptions.selectionType = selectionType;
      if (confirmLabel) state.selectionOptions.confirmLabel = confirmLabel;
      if (cancelLabel) state.selectionOptions.cancelLabel = cancelLabel;
      if (scrollPosition) state.selectionOptions.scrollPosition = scrollPosition;
    },
    moveWeek: (_state, _action: MoveWeekPayload) => {},
    editActivityModalOpen: (state, action: EditActivityModalOpenPayload) => {
      state.editActivityModal.status = 'OPEN';
      state.editActivityModal.data = action.payload;
    },
    editActivityModalClose: (state) => {
      state.editActivityModal.status = 'CLOSED';
      state.editActivityModal.data = null;
    },
    saveActivityFailed: (state) => {
      state.editActivityModal.status = 'OPEN';
    },
    duplicateActivityModalOpen: (state, action: DuplicateActivityPayload) => {
      state.duplicateActivityModal.status = 'OPEN';
      state.duplicateActivityModal.data = action.payload;
    },
    duplicateActivityModalClose: (state) => {
      state.duplicateActivityModal.status = 'CLOSED';
      state.duplicateActivityModal.data = null;
    },
    confirmDuplicateActivity: (state, _action: ConfirmDuplicateActivityPayload) => {
      state.duplicateActivityModal.status = 'SAVING';
    },
    setScrollToDate: (state, action: PayloadAction<string | null>) => {
      state.scrollToDate = action.payload;
    },
  },
});

export const calendarActions = calendarState.actions;
export const {
  loadGoalsForPlans,
  loadTranslationsToNewCurricula,
  setCalendarKeys,
  setMerge,
  selectClass,
  unselectClass,
  setSelectedClasses,
  setUnSelectedClasses,
  deleteActivity,
  saveActivity,
  setSelectionMode,
  setSelectionOptions,
  moveWeek,
  editActivityModalOpen,
  editActivityModalClose,
  saveActivityFailed,
  duplicateActivityModalOpen,
  duplicateActivityModalClose,
  confirmDuplicateActivity,
  setScrollToDate,
} = calendarState.actions;
export default calendarState;
